<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import 'swiper/css';
    import type { ProductBadgeData } from '~/@types/cms';
    import type { AssetData } from '~/@types/cms';

    export interface GalleryProps {
        data: {
            uspTitle?: string;
            // uspSubtitle: string; // currently not in use
            uspText?: string;
            src: string;
            alt?: string;
            productBadge?: ProductBadgeData;
            video?: AssetData;
        }[];
        redCharity?: boolean;
        productName?: string;
        // sku: string; // currently not in use
    }

    const props = defineProps<GalleryProps>();

    const { setMainSwiper, mainSwiper } = useGallery();

    const infoOpen = ref(false);
    const activeSlideIndex = ref(1);
    const thumbsContainer = ref<HTMLDivElement>();

    const unmounting = ref(false);

    const isVertical = ref(false);
    useMediaQuery([
        {
            query: defaultQueries.min.desktopLarge,
            ref: isVertical,
        },
    ]);

    onBeforeUnmount(() => {
        unmounting.value = true;
        mainSwiper.value = undefined;
    });

    const galleryArr = computed(() => (unmounting.value ? [] : props.data));

    const openInfo = () => {
        infoOpen.value = !infoOpen.value;
    };

    const changeActiveIndex = () => {
        if (unmounting.value) {
            return;
        }
        const currentIndex = mainSwiper?.value?.realIndex ?? 0;
        activeSlideIndex.value = currentIndex + 1;

        if (!thumbsContainer.value) return;
        const activeThumb = thumbsContainer.value.querySelectorAll('.gallery-thumb')[currentIndex];
        if (!activeThumb) return;

        const scrollConfig: ScrollToOptions = { top: 0, left: 0, behavior: 'smooth' };
        const { top: activeThumbTop, left: activeThumbLeft } = activeThumb.getBoundingClientRect();
        const { top: thumbsContainerTop, left: thumbsContainerLeft } = thumbsContainer.value.getBoundingClientRect();
        if (isVertical.value) {
            scrollConfig.top = activeThumbTop + activeThumb.clientHeight / 2 - (thumbsContainerTop + thumbsContainer.value.clientHeight / 2);
        } else {
            scrollConfig.left = activeThumbLeft + activeThumb.clientWidth / 2 - (thumbsContainerLeft + thumbsContainer.value.clientWidth / 2);
        }

        thumbsContainer.value.scrollBy(scrollConfig);
    };

    const changeActiveIndexThumb = (currentIndex: number) => {
        if (unmounting.value) {
            return;
        }
        if (mainSwiper.value) mainSwiper.value.slideToLoop(currentIndex);
    };
</script>

<template>
    <div class="mol-gallery flex max-h-[70vh] w-full max-w-full flex-col overflow-hidden lg:gap-10 xl:flex-row xl:items-start">
        <div class="flex max-h-full grow-0 overflow-hidden xl:order-2 xl:w-5/6">
            <nuxt-error-boundary @error="() => {}">
                <swiper
                    class="relative max-h-full max-w-full overflow-hidden"
                    loop
                    :slides-per-view="1"
                    @swiper="setMainSwiper"
                    @slide-change="changeActiveIndex">
                    <swiper-slide
                        v-for="(slide, index) in galleryArr"
                        :key="index">
                        <div class="flex h-full flex-col justify-between">
                            <div class="gallery-image flex justify-center">
                                <template v-if="$slots.default && index === 0">
                                    <slot></slot>
                                </template>
                                <atm-video
                                    v-if="slide.video"
                                    class="aspect-3/2 w-full"
                                    :data="slide.video"
                                    style-size="none"
                                    not-rounded />
                                <atm-image
                                    v-else
                                    class="aspect-3/2"
                                    :src="slide.src"
                                    :alt="slide.alt"
                                    :image-loading="index === 0 ? 'eager' : 'lazy'"
                                    :image-preload="index === 0"
                                    render-as="picture"
                                    sizes="100vw lg:750px xl:1264" />

                                <lazy-atm-gallery-badge-popover
                                    v-if="(slide.productBadge?.product_badge && slide.productBadge.product_badge !== 'none') || redCharity"
                                    :badge="slide.productBadge?.product_badge"
                                    :redCharity="redCharity"
                                    :firstSlide="index === 0" />
                            </div>

                            <div class="gallery-image-info flex h-14">
                                <div
                                    v-if="slide.uspTitle"
                                    class="flex w-full flex-col justify-between bg-woom-grey pl-6 pr-2 lg:rounded-lg"
                                    :class="[infoOpen ? 'absolute bottom-0 z-20 h-auto bg-woom-grey/90' : '']">
                                    <span class="flex h-16 w-full grow-0 items-center justify-between">
                                        <span class="font-bold">{{ slide.uspTitle }}</span>
                                        <button
                                            v-if="slide.uspText"
                                            :class="[
                                                'flex h-12 min-w-12 shrink-0 items-center gap-1 whitespace-nowrap text-xs underline transition-colors hover:text-woom-red',
                                                !infoOpen ? 'gtm-click-product_usp' : '',
                                            ]"
                                            :data-product-name="productName"
                                            @click="openInfo"
                                            :aria-label="infoOpen ? $t('cta.closeGalleryInfo') : undefined">
                                            <template v-if="!infoOpen">
                                                {{ $t('cta.moreInfo') }}
                                                <woom-icon-information-circle class="h-4 w-4 shrink-0 fill-current" />
                                            </template>
                                            <template v-if="infoOpen">
                                                <woom-icon-close class="mx-auto h-4 w-4 shrink-0 fill-current" />
                                            </template>
                                        </button>
                                    </span>
                                    <span
                                        v-if="slide.uspText && infoOpen"
                                        class="max-h-52 overflow-y-auto pb-4">
                                        <renderer-html :content="slide.uspText" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </swiper-slide>
                    <div
                        v-if="!infoOpen"
                        class="absolute bottom-20 z-10 flex items-center gap-2 px-6 text-xs">
                        <!-- TODO: move svg to icons library -->
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24">
                            <path
                                d="M15 3.75A2.252 2.252 0 0 1 17.25 6v12A2.252 2.252 0 0 1 15 20.25H9A2.252 2.252 0 0 1 6.75 18V6A2.252 2.252 0 0 1 9 3.75h6zm0 1.5H9a.75.75 0 0 0-.75.75v12c0 .414.336.75.75.75h6a.75.75 0 0 0 .75-.75V6a.75.75 0 0 0-.75-.75zm7.339.748c.879 0 1.608.695 1.66 1.583L24 16.375c-.053.921-.79 1.626-1.679 1.626l-.089-.002L19.5 18a.75.75 0 0 1 0-1.5h2.772l.03.371.001-.37c.109 0 .193-.072.199-.17L22.5 7.625c-.003-.046-.084-.126-.181-.126l-2.802.001H19.5a.75.75 0 0 1 0-1.5h2.772a.892.892 0 0 1 .067-.002zm-20.664.001.093.003L4.5 6a.75.75 0 0 1 0 1.5H1.727c-.135 0-.222.073-.228.169l.001 8.706c.004.048.082.126.181.126l2.802-.001H4.5a.75.75 0 0 1 0 1.5H1.728a1.674 1.674 0 0 1-1.727-1.581L0 7.625c.053-.922.788-1.626 1.675-1.626zm17.827 3.816c.201 0 .389.078.531.22l1.467 1.468a.747.747 0 0 1 0 1.061l-1.47 1.466a.743.743 0 0 1-.53.22.752.752 0 0 1-.53-1.281l.937-.937-.937-.938a.743.743 0 0 1-.219-.53c0-.2.078-.389.22-.53a.744.744 0 0 1 .531-.219zm-15.002 0a.751.751 0 0 1 .531 1.28l-.937.938.936.937a.752.752 0 0 1 0 1.061.746.746 0 0 1-1.06-.001l-1.467-1.467a.75.75 0 0 1 0-1.06l1.467-1.468a.743.743 0 0 1 .53-.22z"
                                fill="#222"
                                fill-rule="evenodd" />
                        </svg>
                        <span>{{ activeSlideIndex }} </span>/<span>{{ galleryArr.length }}</span>
                    </div>
                </swiper>
            </nuxt-error-boundary>
        </div>

        <div class="relative w-full xl:order-1 xl:h-full xl:w-1/6">
            <div
                class="no-scrollbar flex h-14 gap-1 overflow-x-auto lg:gap-3 xl:h-auto xl:max-h-full xl:flex-col xl:overflow-y-auto"
                ref="thumbsContainer">
                <div
                    v-for="(slide, index) in galleryArr"
                    :key="index"
                    class="gallery-thumb aspect-3/2 !w-20 shrink-0 cursor-pointer overflow-clip lg:rounded-lg xl:!w-full"
                    :class="index === galleryArr.length - 1 ? 'xl:mb-20' : ''"
                    @click="changeActiveIndexThumb(index)">
                    <atm-video-preview
                        v-if="slide.video && isAssetDataYouTubeVideo(slide.video)"
                        :usp-title="slide.uspTitle"
                        gtm-click="gtm-click-galery"
                        :code="slide.video.data.code"
                        :class="['h-full transition-transform', { ' scale-125': index + 1 === activeSlideIndex }]"
                        icon-size="small" />
                    <div :class="['h-full transition-transform', { ' scale-125': index + 1 === activeSlideIndex }]">
                        <atm-image
                            :usp-title="slide.uspTitle"
                            gtm-click="gtm-click-galery"
                            :src="slide.src"
                            sizes="150px md:250px"
                            image-loading="eager" />
                    </div>
                </div>
                <div class="bg-gradient-white pointer-events-none absolute bottom-0 left-0 z-10 hidden h-24 w-full xl:block"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .mol-gallery {
        --info-height: 3rem;
    }
    .gallery-image {
        /* makes sure slide info is shown  */
        max-height: calc(100% - var(--info-height));
    }
    .gallery-image-info {
        min-height: var(--info-height);
    }
    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }
    .no-scrollbar {
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
    }
</style>
